var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch, } from "react-hook-form";
import { Autocomplete, Box, FormControl, FormHelperText, TextField, } from "@mui/material";
import { buildFieldValidation } from "../../validators/field-components";
import { getElementValueKey, reorderChoices, generateChoicesFromRange, } from "../../../../utils/helpers";
import CommentArea from "../common/CommentArea";
import { useElementConditions, useFormLogic, } from "../../context/FormLogicContext";
import Header from "../common/Header";
import { getCommentFieldName, getInitialValueForElement, resolveStoreOthersAsComment, resolveTextUpdateMode, } from "../../../../utils/forms-functions";
import { NONE_VALUE, OTHER_VALUE } from "./constants/special-options";
import OtherOptionInput from "./OtherOptionInput";
import { useClearInvisibleValue } from "../../hooks/useClearInvisibleValue";
import { useChoicesConditions } from "../../hooks/useChoicesConditions";
var DropdownField = function (_a) {
    var element = _a.element, errors = _a.errors;
    var _b = useFormLogic(), globalTextUpdateMode = _b.textUpdateMode, globalStoreOthersAsComment = _b.storeOthersAsComment, globalConstraints = _b.globalConstraints;
    var _c = useFormContext(), unregister = _c.unregister, setValue = _c.setValue, getValues = _c.getValues, trigger = _c.trigger;
    var valueKey = getElementValueKey(element);
    var error = errors[valueKey];
    var _d = useElementConditions(element), isVisible = _d.isVisible, isEnabled = _d.isEnabled, isInteractive = _d.isInteractive, fieldRequired = _d.isRequired;
    var _e = useChoicesConditions(element), choicesVisible = _e.choicesVisible, choicesEnabled = _e.choicesEnabled;
    var validation = useMemo(function () {
        return buildFieldValidation(element, {
            isRequired: fieldRequired,
            readOnly: !isEnabled,
        });
    }, [element, fieldRequired, isEnabled]);
    var textUpdateMode = resolveTextUpdateMode(element, {
        textUpdateMode: globalTextUpdateMode,
    });
    var storeOthersAsComment = resolveStoreOthersAsComment(element, globalStoreOthersAsComment);
    var commentKey = getCommentFieldName(valueKey);
    var commentValue = useWatch({ name: commentKey });
    var isOtherSelectedValue = function (value) {
        if (!element.showOtherItem || !value)
            return false;
        if (value === OTHER_VALUE)
            return true;
        if (value === NONE_VALUE)
            return false;
        if (storeOthersAsComment)
            return false;
        return !choicesValues.includes(value);
    };
    var readOtherText = function (value) {
        if (!isOtherSelectedValue(value))
            return "";
        if (storeOthersAsComment)
            return String(commentValue !== null && commentValue !== void 0 ? commentValue : "");
        return value === OTHER_VALUE ? "" : String(value);
    };
    // A validação roda no mesmo tick do commit, quando o useWatch ainda devolve
    // o valor anterior: aqui é preciso ler o estado vivo do formulário.
    var readOtherTextLive = function (value) {
        var _a;
        if (!isOtherSelectedValue(value))
            return "";
        if (storeOthersAsComment)
            return String((_a = getValues(commentKey)) !== null && _a !== void 0 ? _a : "");
        return value === OTHER_VALUE ? "" : String(value);
    };
    var clearOtherText = function () {
        if (storeOthersAsComment && commentValue) {
            setValue(commentKey, "", { shouldDirty: true });
        }
    };
    var initialValue = getInitialValueForElement(element);
    var valueKeys = useMemo(function () {
        return element.showCommentArea || storeOthersAsComment
            ? [valueKey, commentKey]
            : [valueKey];
    }, [valueKey, commentKey, element.showCommentArea, storeOthersAsComment]);
    useClearInvisibleValue({
        isVisible: isVisible,
        valueKeys: valueKeys,
        unregister: unregister,
    });
    var choices = useMemo(function () {
        var baseChoices = (element.choices || []);
        var choicesFormatted = baseChoices.map(function (choice) {
            if (typeof choice === "string") {
                return { value: choice, text: choice };
            }
            return choice;
        });
        if (element.choicesMax !== undefined) {
            var generatedChoices = generateChoicesFromRange(element.choicesMax, element.choicesMin, element.choicesStep);
            choicesFormatted = __spreadArray(__spreadArray([], choicesFormatted, true), generatedChoices, true);
        }
        var orderedChoices = choicesFormatted;
        if (element.choicesOrder) {
            orderedChoices = reorderChoices(choicesFormatted, element.choicesOrder);
        }
        if (element.showNoneItem) {
            orderedChoices = __spreadArray(__spreadArray([], orderedChoices, true), [
                { value: "none", text: element.noneText || "Nenhum" },
            ], false);
        }
        return orderedChoices;
    }, [element]);
    var choicesValues = useMemo(function () {
        return choices.map(function (choice) { return choice.value; });
    }, [choices]);
    var baseChoicesValues = useMemo(function () {
        var baseChoices = (element.choices || []);
        return baseChoices.map(function (choice) {
            return typeof choice === "string" ? choice : choice.value;
        });
    }, [element.choices]);
    useEffect(function () {
        if (!choicesVisible) {
            var currentValue = getValues(valueKey);
            if (baseChoicesValues.includes(currentValue)) {
                setValue(valueKey, "");
            }
        }
    }, [choicesVisible, baseChoicesValues, valueKey, getValues, setValue]);
    var hasOther = element.showOtherItem;
    return (_jsx(_Fragment, { children: isVisible ? (_jsx(Box, __assign({ className: "component-wrapper" }, { children: _jsxs(FormControl, __assign({ fullWidth: true, error: !!error, sx: { mb: 3 } }, { children: [_jsx(Header, { element: element, error: !!error, isRequired: fieldRequired }), _jsx(Controller, { name: valueKey, defaultValue: initialValue, rules: {
                            validate: function (value) {
                                var _a;
                                var result = validation.safeParse(value);
                                if (!result.success) {
                                    return ((_a = result.error.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || "Valor inválido";
                                }
                                // Espelha supportGoNextPageError (question_baseselect.ts:577)
                                if (isOtherSelectedValue(value) &&
                                    !readOtherTextLive(value)) {
                                    return (element.otherErrorText ||
                                        "Por favor, insira o outro valor.");
                                }
                                return true;
                            },
                        }, render: function (_a) {
                            var field = _a.field;
                            var fieldValue = field.value || "";
                            var isOtherSelected = isOtherSelectedValue(fieldValue);
                            var committedOtherText = readOtherText(fieldValue);
                            var visibleChoices = choicesVisible
                                ? choices
                                : choices.filter(function (choice) { return !baseChoicesValues.includes(choice.value); });
                            var autocompleteOptions = hasOther
                                ? __spreadArray(__spreadArray([], visibleChoices, true), [
                                    {
                                        value: OTHER_VALUE,
                                        text: element.otherText || "Outro",
                                    },
                                ], false) : visibleChoices;
                            // Sempre o marcador quando "Outro" está selecionado: usar o
                            // texto digitado faz o lookup falhar e o Autocomplete some.
                            var selectedValue = isOtherSelected
                                ? OTHER_VALUE
                                : fieldValue;
                            var selectedOption = autocompleteOptions.find(function (option) { return option.value === selectedValue; });
                            return (_jsxs(_Fragment, { children: [_jsx(Autocomplete, { options: autocompleteOptions, value: selectedOption !== null && selectedOption !== void 0 ? selectedOption : null, disabled: !isInteractive, disableClearable: element.allowClear !== undefined ? true : false, getOptionLabel: function (option) { return option.text; }, isOptionEqualToValue: function (option, value) {
                                            return option.value === value.value;
                                        }, getOptionDisabled: function (option) {
                                            return !choicesEnabled &&
                                                baseChoicesValues.includes(option.value);
                                        }, onChange: function (_, newOption) {
                                            var newValue = (newOption === null || newOption === void 0 ? void 0 : newOption.value) || "";
                                            clearOtherText();
                                            field.onChange(newValue);
                                        }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { name: field.name, label: element.placeholder || "Selecione...", error: !!error, inputRef: field.ref, onBlur: field.onBlur }))); } }), hasOther && isOtherSelected && (_jsx(OtherOptionInput, { committedValue: committedOtherText, textUpdateMode: textUpdateMode, isEnabled: isInteractive, label: element.otherText || "Outro", placeholder: element.otherPlaceholder || "Digite aqui...", maxLength: globalConstraints.maxOthersLength, sx: { mt: 2 }, onCommit: function (text) {
                                            if (storeOthersAsComment) {
                                                setValue(commentKey, text, { shouldDirty: true });
                                                void trigger(valueKey);
                                                field.onChange(OTHER_VALUE);
                                                return;
                                            }
                                            field.onChange(text.length > 0 ? text : OTHER_VALUE);
                                        } }))] }));
                        } }), error && (_jsx(FormHelperText, { children: error.message })), element.showCommentArea && (_jsx(CommentArea, { element: element, disabled: !isInteractive }))] })) }))) : null }));
};
export default DropdownField;
